import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Handshake, ArrowRight } from 'lucide-react';
import { FaLeaf, FaStore, FaBookOpen, FaBullhorn, FaHandshake, FaSeedling, FaEnvelopeOpenText, FaClipboardList, FaCheckCircle, FaSync } from 'react-icons/fa';
import './ServiceDetail.css';

const ACCENT = '#558b2f';
const ACCENT_LIGHT = '#8bc34a';
const ACCENT_RGB = '85,139,47';

const benefits = [
  {
    emoji: <FaLeaf size={22} />,
    title: 'Quality Plant Supply',
    desc: 'Access a wide range of healthy indoor plants, outdoor plants, flowering plants, and landscaping plants.'
  },
  {
    emoji: <FaStore size={22} />,
    title: 'Nursery Setup Assistance',
    desc: 'Guidance for nursery planning, product selection, display arrangements, and business setup.'
  },
  {
    emoji: <FaBookOpen size={22} />,
    title: 'Training & Knowledge Support',
    desc: 'Learn plant care, customer handling, inventory management, and nursery operations.'
  },
  {
    emoji: <FaBullhorn size={22} />,
    title: 'Marketing Guidance',
    desc: 'Support for local promotions, branding activities, and digital marketing initiatives.'
  },
  {
    emoji: <FaHandshake size={22} />,
    title: 'Ongoing Business Support',
    desc: 'Regular assistance and consultation to help improve business performance and customer satisfaction.'
  },
  {
    emoji: <FaSeedling size={22} />,
    title: 'Industry Experience',
    desc: 'Benefit from practical nursery experience and proven plant business management practices.'
  },
];

const steps = [
  {
    num: '01',
    icon: <FaEnvelopeOpenText size={20} />,
    title: 'Submit Your Enquiry',
    desc: 'Contact our team and share your interest in starting a nursery franchise.'
  },
  {
    num: '02',
    icon: <FaHandshake size={20} />,
    title: 'Business Discussion',
    desc: 'Discuss location, investment plans, and business goals with our experts.'
  },
  {
    num: '03',
    icon: <FaClipboardList size={20} />,
    title: 'Planning & Setup',
    desc: 'Receive guidance for nursery setup, plant selection, and operational planning.'
  },
  {
    num: '04',
    icon: <FaLeaf size={20} />,
    title: 'Launch Your Nursery',
    desc: 'Start your nursery business with continued support from the Nisarg Nursery team.'
  },
];

const stats = [
  { val: <FaSeedling size={28} />, label: 'Quality Plant Supply' },
  { val: <FaHandshake size={28} />, label: 'Business Support' },
  { val: <FaStore size={28} />, label: 'Nursery Expertise' },
  { val: <FaLeaf size={28} />, label: 'Growth Opportunities' },
];

export default function FranchiseModelPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Franchise Model — Nisarg Nursery';
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.sdp-observe').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="sdp-wrapper"
      style={{ '--accent': ACCENT, '--accent-light': ACCENT_LIGHT, '--accent-dark': '#bf360c', '--accent-rgb': ACCENT_RGB }}
    >
      {/* ════ HERO ════ */}
      <section className="sdp-hero">
        <div className="sdp-hero-bg">
          <img src="/images/O tree1.jpg" alt="Franchise Model" className="sdp-hero-img" />
          <div className="sdp-hero-overlay" />
        </div>
        <div className="sdp-hero-content">
          <nav className="sdp-breadcrumb">
            <Link to="/">Home</Link> › <Link to="/services">Services</Link> › <span>Franchise Model</span>
          </nav>
          <div className="sdp-service-tag">
            <Handshake size={16} /> <FaLeaf /> Nursery Franchise Opportunity
          </div>
          <span className="sdp-hero-icon"><Handshake size={56} strokeWidth={1.5} /></span>
          <h1 className="sdp-hero-title">
            Start Your Own <span className="accent-word">Plant Nursery Business</span>
          </h1>
          <p className="sdp-hero-desc">
            Partner with Nisarg Nursery and explore a rewarding nursery franchise opportunity. Get access to quality plants, business guidance, plant sourcing support, and nursery management expertise to grow your green business successfully.
          </p>
          <div className="sdp-hero-cta">
            <Link to="/contact" className="sdp-btn-primary">
              Apply for Franchise <ArrowRight size={16} />
            </Link>
            <Link to="/services" className="sdp-btn-outline">← Back to Services</Link>
          </div>
        </div>
        <div className="sdp-hero-scroll">
          <span>SCROLL</span>
          <div className="sdp-scroll-line" />
        </div>
      </section>

      {/* ── STATS ── */}
      <div className="sdp-stats">
        {stats.map(s => (
          <div key={s.label} className="sdp-stat sdp-observe">
            <span className="sdp-stat-val">{s.val}</span>
            <span className="sdp-stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      <section className="sdp-section sdp-overview">
        <div className="sdp-section-inner">
          <div className="sdp-overview-grid">
            <div className="sdp-overview-left sdp-observe">
              <div className="sdp-overview-img-wrap">
                <img src="/images/mint.jpg" alt="Franchise" className="sdp-overview-img" />
                <div className="sdp-overview-badge">
                  <div className="sdp-overview-badge-dot" />
                  Franchises Accepting Now
                </div>
              </div>
            </div>
            <div className="sdp-overview-right sdp-observe">
              <div className="sdp-section-tag"><FaHandshake /> Nursery Business Opportunity</div>
              <h2 className="sdp-section-title">Build Your Future With,<br />A Trusted Nursery Partner</h2>
              <div className="sdp-overview-text">
                <p>Nisarg Nursery offers franchise opportunities for entrepreneurs, plant enthusiasts, and business owners looking to enter the growing gardening and landscaping industry with professional guidance and support.</p>
                <p>From plant sourcing and nursery setup to staff training and operational assistance, we help franchise partners establish a professional plant nursery business focused on quality products and customer satisfaction.</p>
              </div>
              <div className="sdp-features-list">
                {[
                  'Plant Supply Support',
                  'Nursery Setup Guidance',
                  'Business Training',
                  'Marketing Assistance',
                  'Operational Support',
                  'Growth Partnership'
                ].map(f => (
                  <div key={f} className="sdp-feature-item">
                    <div className="sdp-feature-icon"><FaCheckCircle /></div>
                    {f}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className="sdp-section sdp-observe">
        <div className="sdp-section-inner">
          <div className="sdp-section-tag"><FaCheckCircle /> Why Partner With Us</div>
          <h2 className="sdp-section-title">Benefits Of Joining<br />Nisarg Nursery Franchise</h2>
          <div className="sdp-benefits-grid">
            {benefits.map(b => (
              <div key={b.title} className="sdp-benefit-card sdp-observe">
                <span className="sdp-benefit-emoji">{b.emoji}</span>
                <h3 className="sdp-benefit-title">{b.title}</h3>
                <p className="sdp-benefit-desc">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="sdp-section sdp-process sdp-observe">
        <div className="sdp-section-inner">
          <div className="sdp-section-tag"><FaSync /> How It Works</div>
          <h2 className="sdp-section-title">Simple Steps To Start<br />Your Nursery Business</h2>
          <div className="sdp-process-steps">
            {steps.map(s => (
              <div key={s.num} className="sdp-step sdp-observe">
                <div className="sdp-step-num">{s.num}</div>
                <div className="sdp-step-icon">{s.icon}</div>
                <h3 className="sdp-step-title">{s.title}</h3>
                <p className="sdp-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <div className="sdp-cta sdp-observe">
        <span className="sdp-cta-icon"><FaHandshake size={28} /></span>
        <h2 className="sdp-cta-title">Ready To Start Your Green Business?</h2>
        <p className="sdp-cta-desc">Connect with Nisarg Nursery today to learn more about our nursery franchise opportunities, plant business support, and partnership programs.</p>
        <div className="sdp-cta-btns">
          <Link to="/contact" className="sdp-btn-primary">
            Apply Now
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
          </Link>
          <Link to="/services" className="sdp-btn-outline">← All Services</Link>
        </div>
      </div>
    </div>
  );
}
